import { useMemo } from 'react';

import {
  addCartItems,
  buildCartItem,
  clearCart,
  removeCartItems,
  setItemExclusions,
} from '../cartSlice';
import {
  type CatalogCheckState,
  descendantIds,
  findCoveringRoot,
  isLeafNode,
  subtreeIds,
} from '../utils/catalogSelection';

import type { CspCatalogNode, CspCatalogTreeNode } from '@/shared/types/userStandard';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/redux';

function toTreeNode(node: CspCatalogNode | CspCatalogTreeNode): CspCatalogTreeNode {
  return 'children' in node ? node : { ...node, children: [], hasChildren: false };
}

/**
 * Selection-cart operations shared by the set tree, search results, and preview panel.
 *
 * Tri-state exclusion (Decision 2b): checking a branch selects its whole subtree as a single root;
 * unchecking a descendant under that root records it (and its subtree) in the root's exclusion set
 * rather than fragmenting the selection. Toggling a leaf, or a search hit (no children loaded), adds a
 * single-node entry. The optional `state` is the row's current tri-state as computed by the tree; when
 * omitted (e.g. flat search rows) it is derived from cart membership.
 */
export function useCatalogCart() {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state) => state.standardsCart.items);
  const selectedIds = useMemo(() => new Set(Object.keys(items)), [items]);

  const isInCart = (id: string) => selectedIds.has(id);

  const selectFresh = (node: CspCatalogTreeNode) => {
    if (isLeafNode(node)) {
      dispatch(addCartItems([buildCartItem(node, false)]));
    } else {
      dispatch(removeCartItems(descendantIds(node)));
      dispatch(addCartItems([buildCartItem(node, true)]));
    }
  };

  const toggleNode = (input: CspCatalogNode | CspCatalogTreeNode, state?: CatalogCheckState) => {
    const node = toTreeNode(input);
    const id = node.id;
    const resolved: CatalogCheckState = state ?? (items[id] ? 'on' : 'off');

    if (resolved === 'on') {
      // Turning the node off.
      if (items[id]) {
        // Its own entry (a root or single) → drop it and any nested entries.
        dispatch(removeCartItems(subtreeIds(node)));
        return;
      }
      const root = findCoveringRoot(items, node);
      if (root) {
        // On via a covering branch → exclude this node's subtree from that root.
        const excluded = new Set(root.excludedIds);
        const subsumed = new Set(descendantIds(node));
        for (const x of [...excluded]) if (subsumed.has(x)) excluded.delete(x);
        excluded.add(id);
        dispatch(setItemExclusions({ id: root.node.id, excludedIds: [...excluded] }));
      }
      return;
    }

    // Turning the node on (currently off or partial).
    if (items[id]) {
      // A partial root with exclusions → re-select its whole subtree.
      dispatch(setItemExclusions({ id, excludedIds: [] }));
      return;
    }
    const root = findCoveringRoot(items, node);
    if (root && root.excludedIds.includes(id)) {
      // Re-include a directly-excluded node (and anything nested under it).
      const excluded = new Set(root.excludedIds);
      const subtree = new Set(subtreeIds(node));
      for (const x of [...excluded]) if (subtree.has(x)) excluded.delete(x);
      dispatch(setItemExclusions({ id: root.node.id, excludedIds: [...excluded] }));
      return;
    }
    // Not covered (or off beneath an excluded ancestor) → add as its own selection.
    selectFresh(node);
  };

  // Drop every cart entry for the set — roots and any separate deep entries (e.g. a re-picked grandchild).
  const clearSetItems = (standardSetId: string) => {
    const ids = Object.values(items)
      .filter((item) => item.node.standard_set_id === standardSetId)
      .map((item) => item.node.id);
    if (ids.length > 0) dispatch(removeCartItems(ids));
  };

  // Whole-collection select: drop every cart entry that belongs to the set, then add each root as a
  // fresh selection. Clears any prior partial exclusions so the set comes in fully checked.
  const selectWholeSet = (standardSetId: string, roots: CspCatalogTreeNode[]) => {
    clearSetItems(standardSetId);
    for (const root of roots) selectFresh(root);
  };

  const removeNode = (id: string) => dispatch(removeCartItems([id]));
  const clear = () => dispatch(clearCart());

  return { items, selectedIds, isInCart, toggleNode, selectWholeSet, removeNode, clear };
}
